import React, { useMemo, ReactElement, useRef, useLayoutEffect, useState } from 'react';
import { parse, Node } from './utils/rich-parser';
import RichTextContext from './context';
import Emoji from './emoji';
import Url from './url';
import Dref from './dref';

import { registerComponent } from './registry';
import Judge, { JudgeChecker } from './plugins/Judge';
import Member, { MemberCheck } from './plugins/Member';
import T12Icon, { T2IconCheck } from './plugins/T12Icon';
import TextDefault, { TextDefaultChecker } from './plugins/TextDefault';

registerComponent(JudgeChecker, Judge);
registerComponent(JudgeChecker, Judge, true);
registerComponent(MemberCheck, Member);
registerComponent(T2IconCheck, T12Icon);
registerComponent(TextDefaultChecker, TextDefault, true);

export interface RichTextProps {
  text: string;
  style?: React.CSSProperties;
  emojiStyle?: React.CSSProperties;
}

function renderComponent(node: Node, richTextProps: any, index: number): ReactElement<any> {
  switch (node.type) {
    case 'text':
      return (
        <span style={{ whiteSpace: 'pre-wrap' }} key={index}>
          {node.text}
        </span>
      );
    case 'emoji':
      return <Emoji key={index} node={node} richTextProps={richTextProps} />;
    case 'url':
      return <Url key={index} node={node} richTextProps={richTextProps} />;
    case 'dref':
      return (
        <Dref key={index} node={node} richTextProps={richTextProps}>
          {node.children.map((n, idx) => renderComponent(n, richTextProps, idx))}
        </Dref>
      );
    case 'fragment':
      return <>{node.children.map((n, idx) => renderComponent(n, richTextProps, idx))}</>;
    default:
      return null;
  }
}

function RichText({ text, style = {}, emojiStyle }: RichTextProps) {
  const ast = useMemo(() => parse(text), [text]);
  const elRef = useRef(undefined);
  const [contextValue, setContextValue] = useState({ fontSize: 14, lineHeight: 14 * 1.25 });

  const richTextProps = {
    style,
    emojiStyle,
  };

  useLayoutEffect(() => {
    if (elRef.current) {
      const css = getComputedStyle(elRef.current);
      const fontSize = Number(css['font-size'].replace('px', '')) || contextValue.fontSize;
      const lineHeight = Number(css['line-height'].replace('px', '')) || contextValue.lineHeight;
      setContextValue({
        fontSize,
        lineHeight,
      });
    }
  }, []);

  return (
    <RichTextContext.Provider value={contextValue}>
      <span ref={elRef} style={style}>
        {renderComponent(ast, richTextProps, 0)}
      </span>
    </RichTextContext.Provider>
  );
}

export default React.memo(RichText);
